import { TYPES } from '@/engine/ioc';

import { SceneItemKind } from './interfaces';

import { injectable, inject } from 'inversify';
import type { ICasePersistent, ICaseState } from './CasePersistance/CasePersistenceHub';
import type { ViewsService } from './Views.service';
import type { IObjectViewModel, CommissurePointViewData, ObjectData } from './interfaces';
import type { IStrategyByKindProvider } from './LoadStateStrategiesProvider';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity.ts';

export type StateMetadata = Array<CommissurePointViewData>;

@injectable()
export class CommissurePointsResolver implements ICasePersistent {
  private readonly _viewsService: ViewsService;

  private readonly _strategyByKindProvider: IStrategyByKindProvider;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IStrategyByKindProvider) strategyByKindProvider: IStrategyByKindProvider
  ) {
    this._viewsService = viewsService;
    this._strategyByKindProvider = strategyByKindProvider;
  }

  public saveState(): Promise<ICaseState> {
    const views: Array<ViewEntity> = this._viewsService.views;
    const dataToSave: StateMetadata = [];

    views.forEach((view: ViewEntity): void => {
      const viewDataToSave: CommissurePointViewData = {
        viewDescriptor: view.getDescriptor(),
        objects: []
      };

      const addedObjects: Array<IObjectViewModel> = view.getObjects().filter((obj: IObjectViewModel) => {
        return obj.kind === SceneItemKind.CommissurePoint && !obj.imported;
      });

      addedObjects?.forEach((obj: IObjectViewModel): void => {
        viewDataToSave.objects.push({
          descriptor: obj.descriptor,
          label: obj.label,
          kind: obj.kind,
          phaseId: obj.phaseId,
          zoomFactor: obj.zoomFactor,
          attachments: obj.attachments,
          color: obj.color
        });
      });
      dataToSave.push(viewDataToSave);
    });

    const state: ICaseState<StateMetadata> = {
      version: '1.0',
      metadata: dataToSave
    };

    return Promise.resolve(state);
  }

  public loadState(metadata: unknown): Promise<void> {
    const viewsIndicationsData = metadata as StateMetadata;

    viewsIndicationsData.forEach((viewData: CommissurePointViewData): void => {
      const view: ViewEntity | undefined = this._viewsService.getViewByDescriptor(viewData.viewDescriptor);
      if (!view) return;

      viewData.objects
        .filter((obj: ObjectData): boolean => obj.kind === SceneItemKind.CommissurePoint)
        .forEach((objectsData: ObjectData): void =>
          this._strategyByKindProvider.getStrategy(objectsData.kind).loadState(view, objectsData)
        );
    });
    return Promise.resolve();
  }
}
